import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import ReactApexChart from 'react-apexcharts';
import AnalyticsService from '../../../services/AnalyticsService';
import { selectIsAdmin } from '../../../store/selectors/AuthSelectors';
import {
  PageHeader, Card, StatTile, Badge, Dot, Table, Tr, Td, EmptyState, Select,
} from '../../ui';

const TABS = [
  ['overview', 'Overview', 'fa-chart-line'],
  ['heatmap', 'Heatmaps', 'fa-table-cells'],
  ['location', 'Location', 'fa-globe'],
  ['devices', 'Devices', 'fa-display'],
  ['bandwidth', 'Bandwidth', 'fa-wifi'],
  ['revenue', 'Revenue', 'fa-dollar-sign'],
];

const fmtBytes = (b) => {
  if (!b) return '0 B';
  const k = 1024, s = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${parseFloat((b / Math.pow(k, i)).toFixed(2))} ${s[i]}`;
};
const fmtDuration = (sec) => {
  if (!sec) return '0 min';
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
};
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Shared apex options tuned for the light theme
const axisColor = '#98a1b5';
const baseChart = {
  chart: { toolbar: { show: false }, fontFamily: 'Inter, sans-serif', foreColor: axisColor },
  colors: ['#f26a1b', '#22c55e', '#4b5568', '#f59e0b'],
  dataLabels: { enabled: false },
  stroke: { curve: 'smooth', width: 2.5 },
  grid: { borderColor: '#e6e8f1', strokeDashArray: 4 },
  legend: { position: 'top', horizontalAlign: 'left' },
  tooltip: { theme: 'light' },
};
const areaChart = {
  ...baseChart,
  fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05 } },
};

const Analytics = ({ isAdmin }) => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [tab, setTab] = useState('overview');

  const [dashboard, setDashboard] = useState(null);
  const [realtime, setRealtime] = useState([]);
  const [live, setLive] = useState(null);       // full real-time snapshot, refreshed every 8s
  const [watchTime, setWatchTime] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [location, setLocation] = useState([]);
  const [devices, setDevices] = useState({ devices: [], browsers: [] });
  const [bandwidth, setBandwidth] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [heatmap, setHeatmap] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const params = { period };
      const safe = (p) => p.then((r) => r).catch(() => ({ success: false }));
      const [d, rt, wt, en, loc, dev, bw, rev, hm] = await Promise.all([
        safe(AnalyticsService.getDashboardSummary(params)),
        safe(AnalyticsService.getRealtimeViewers(params)),
        safe(AnalyticsService.getWatchTimeAnalytics(params)),
        safe(AnalyticsService.getEngagementAnalytics(params)),
        safe(AnalyticsService.getLocationAnalytics(params)),
        safe(AnalyticsService.getDeviceAnalytics(params)),
        safe(AnalyticsService.getBandwidthAnalytics(params)),
        isAdmin ? safe(AnalyticsService.getRevenueAnalytics(params)) : Promise.resolve({ success: true, data: null }),
        safe(AnalyticsService.getHeatmapData(params)),
      ]);
      if (cancelled) return;
      if (d.success) setDashboard(d.data);
      if (rt.success) setRealtime(rt.data || []);
      if (wt.success) setWatchTime(wt.data);
      if (en.success) setEngagement(en.data);
      if (loc.success) setLocation(loc.data || []);
      if (dev.success) setDevices(dev.data || { devices: [], browsers: [] });
      if (bw.success) setBandwidth(bw.data);
      if (rev.success) setRevenue(rev.data);
      if (hm.success) setHeatmap(hm.data || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [period, isAdmin]);

  // Live poll — real viewer counts every 8s, independent of the period filter.
  useEffect(() => {
    let alive = true;
    const pull = async () => {
      const r = await AnalyticsService.getRealtime();
      if (alive && r && r.success) setLive(r.data);
    };
    pull();
    const id = setInterval(pull, 8000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // ---- derived series ----
  const watchSeries = useMemo(() => {
    const ot = watchTime?.over_time || [];
    return {
      categories: ot.map((r) => r.date?.slice(5)),
      data: ot.map((r) => +(r.watch_seconds / 3600).toFixed(2)),
    };
  }, [watchTime]);

  const bwSeries = useMemo(() => {
    const ot = bandwidth?.over_time || [];
    return {
      categories: ot.map((r) => r.date?.slice(5)),
      data: ot.map((r) => +((r.transferred || 0) / 1073741824).toFixed(3)),
    };
  }, [bandwidth]);

  const revSeries = useMemo(() => {
    const ot = revenue?.over_time || [];
    return {
      categories: ot.map((r) => r.date?.slice(5)),
      data: ot.map((r) => +((r.daily_revenue || 0) / 100).toFixed(2)),
    };
  }, [revenue]);

  const engagementTotal = engagement
    ? (engagement.likes || 0) + (engagement.shares || 0) + (engagement.comments || 0) + (engagement.reactions || 0)
    : 0;

  if (loading && !dashboard) {
    return (
      <div className="tw-py-20 tw-text-center">
        <i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" />
        <p className="tw-mt-3 tw-text-content-muted">Loading analytics…</p>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Analytics & Insights"
        subtitle="Real streaming performance, audience and revenue"
        actions={
          <Select value={period} onChange={(e) => setPeriod(e.target.value)} className="tw-w-40">
            <option value="today">Today</option>
            <option value="week">Last 7 days</option>
            <option value="month">This month</option>
            <option value="year">This year</option>
          </Select>
        }
      />

      <div className="tw-mb-6 tw-flex tw-flex-wrap tw-gap-1 tw-border-b tw-border-line">
        {TABS.filter((t) => t[0] !== 'revenue' || isAdmin).map(([id, label, icon]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5 tw-text-sm tw-font-semibold tw-transition ${
              tab === id ? 'tw-border-b-2 tw-border-brand tw-text-brand' : 'tw-text-content-muted hover:tw-text-heading'
            }`}
          >
            <i className={`fa-solid ${icon}`} /> {label}
          </button>
        ))}
      </div>

      {loading && <div className="tw-mb-4 tw-text-xs tw-text-content-faint"><i className="fa-solid fa-spinner fa-spin tw-mr-1" /> refreshing…</div>}

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="tw-space-y-6">
          <div className="tw-grid tw-grid-cols-2 tw-gap-4 lg:tw-grid-cols-4">
            <StatTile icon="fa-eye" tone="good" value={live?.total_viewers ?? dashboard?.total_live_viewers ?? 0} label="Viewers right now" />
            <StatTile icon="fa-satellite-dish" tone="live" value={live?.active_streams ?? 0} label="Streams with viewers" />
            <StatTile icon="fa-video" tone="brand" value={dashboard?.total_streams ?? 0} label="Total Streams" />
            <StatTile icon="fa-users" tone="info" value={(dashboard?.total_all_time_views ?? 0).toLocaleString()} label="All-time Views" />
          </div>

          {/* LIVE NOW */}
          <Card
            title={<span className="tw-flex tw-items-center tw-gap-2"><Dot tone="live" pulse /> Live now</span>}
            subtitle="Concurrent viewers, counted from real player activity · refreshes every 8s"
          >
            {(!live || live.total_viewers === 0) ? (
              <EmptyState icon="fa-satellite-dish" title="No one watching right now"
                hint="Concurrent viewers appear here the moment someone opens a stream — counted from real player activity, updated every few seconds." />
            ) : (
              <div className="tw-space-y-5">
                {/* per-stream */}
                <Table head={['Stream', 'Viewers', 'Avg. watching', 'Top locations', 'Devices']}>
                  {live.streams.filter((s) => s.viewers > 0).map((s) => (
                    <Tr key={s.stream_id}>
                      <Td className="tw-font-semibold tw-text-heading">{s.name}</Td>
                      <Td><span className="tw-text-lg tw-font-extrabold tw-text-brand">{s.viewers}</span></Td>
                      <Td className="tw-text-content-muted">{fmtDuration(s.avg_watch_seconds)}</Td>
                      <Td className="tw-text-content-muted tw-text-xs">
                        {Object.entries(s.by_country || {}).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([c, n]) => `${c} ${n}`).join(' · ') || '—'}
                      </Td>
                      <Td className="tw-text-content-muted tw-text-xs">
                        {Object.entries(s.by_device || {}).sort((a, b) => b[1] - a[1]).map(([d, n]) => `${d} ${n}`).join(' · ') || '—'}
                      </Td>
                    </Tr>
                  ))}
                </Table>

                {/* aggregated breakdowns */}
                <div className="tw-grid tw-gap-4 sm:tw-grid-cols-3">
                  {[['Locations', live.by_country], ['Devices', live.by_device], ['Sources', live.by_referrer]].map(([label, arr]) => (
                    <div key={label} className="tw-rounded-xl tw-bg-sunken tw-p-3">
                      <div className="tw-mb-2 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">{label}</div>
                      {(!arr || !arr.length) ? <div className="tw-text-xs tw-text-content-faint">—</div> : (
                        <div className="tw-space-y-1">
                          {arr.slice(0, 5).map((x) => (
                            <div key={x.name} className="tw-flex tw-justify-between tw-text-sm">
                              <span className="tw-truncate tw-text-content">{x.name || 'Unknown'}</span>
                              <span className="tw-font-semibold tw-text-heading">{x.count}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* 60-min concurrent trend */}
                {live.trend && live.trend.length > 1 && (
                  <div>
                    <div className="tw-mb-1 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wider tw-text-content-faint">Last hour</div>
                    <ReactApexChart
                      type="area" height={180}
                      options={{
                        ...areaChart,
                        chart: { ...areaChart.chart, sparkline: { enabled: false }, animations: { enabled: false } },
                        xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
                        yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (v) => Math.round(v) } },
                      }}
                      series={[{ name: 'Concurrent viewers', data: live.trend.map((p) => [new Date(p.time).getTime(), p.viewers]) }]}
                    />
                  </div>
                )}
              </div>
            )}
          </Card>

          <div className="tw-grid tw-gap-6 lg:tw-grid-cols-2">
            <Card title="Watch Time (hours / day)">
              {watchSeries.data.length === 0 ? (
                <EmptyState icon="fa-clock" title="No watch-time data yet" hint="Populated as viewers watch your streams via the player." />
              ) : (
                <ReactApexChart
                  type="area" height={280}
                  options={{ ...areaChart, xaxis: { categories: watchSeries.categories } }}
                  series={[{ name: 'Watch hours', data: watchSeries.data }]}
                />
              )}
              <div className="tw-mt-3 tw-text-center tw-text-sm">
                <span className="tw-text-content-muted">Avg session: </span>
                <strong className="tw-text-heading">{fmtDuration(watchTime?.avg_session_duration || 0)}</strong>
                <span className="tw-mx-2 tw-text-content-faint">·</span>
                <span className="tw-text-content-muted">Unique viewers: </span>
                <strong className="tw-text-heading">{watchTime?.unique_viewers || 0}</strong>
              </div>
            </Card>

            <Card title="Engagement">
              {engagementTotal === 0 ? (
                <EmptyState icon="fa-heart" title="No engagement recorded" hint="Likes, shares and comments will show here once viewers interact." />
              ) : (
                <>
                  <div className="tw-mb-2 tw-text-center">
                    <div className="tw-text-3xl tw-font-extrabold tw-text-brand">{engagement?.engagement_rate || 0}%</div>
                    <div className="tw-text-sm tw-text-content-muted">Overall engagement rate</div>
                  </div>
                  <ReactApexChart
                    type="donut" height={210}
                    options={{
                      labels: ['Likes', 'Shares', 'Comments', 'Reactions'],
                      colors: ['#f26a1b', '#22c55e', '#4b5568', '#f59e0b'],
                      legend: { position: 'bottom' }, stroke: { width: 0 },
                      dataLabels: { enabled: false },
                    }}
                    series={[engagement?.likes || 0, engagement?.shares || 0, engagement?.comments || 0, engagement?.reactions || 0]}
                  />
                </>
              )}
            </Card>
          </div>
        </div>
      )}

      {/* HEATMAP */}
      {tab === 'heatmap' && (
        <Card title="Viewership Heatmap" subtitle="Sessions by day of week and hour of day">
          {(!heatmap.length || heatmap.every((row) => row.every((v) => !v))) ? (
            <EmptyState icon="fa-table-cells" title="No heatmap data yet" hint="Builds up from viewer watch sessions over time." />
          ) : (
            <ReactApexChart
              type="heatmap" height={360}
              options={{
                dataLabels: { enabled: false },
                colors: ['#f26a1b'],
                xaxis: { categories: Array.from({ length: 24 }, (_, i) => `${i}:00`) },
                grid: { borderColor: '#e6e8f1' },
                plotOptions: { heatmap: { radius: 4, colorScale: { min: 0 } } },
              }}
              series={['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((name, i) => ({
                name, data: heatmap[i] || Array(24).fill(0),
              }))}
            />
          )}
        </Card>
      )}

      {/* LOCATION */}
      {tab === 'location' && (
        <Card title="Audience Location" noPad>
          {location.length === 0 ? (
            <EmptyState icon="fa-globe" title="No location data yet" hint="Viewer countries are resolved from IP as sessions are recorded." />
          ) : (
            <Table head={['Country', 'Watch Time', 'Sessions', 'Unique Viewers']}>
              {location.map((l, i) => (
                <Tr key={i}>
                  <Td className="tw-font-semibold tw-text-heading"><i className="fa-solid fa-location-dot tw-mr-2 tw-text-content-faint" />{l.country || 'Unknown'}</Td>
                  <Td>{fmtDuration(l.watch_time)}</Td>
                  <Td>{l.sessions}</Td>
                  <Td>{l.unique_viewers}</Td>
                </Tr>
              ))}
            </Table>
          )}
        </Card>
      )}

      {/* DEVICES */}
      {tab === 'devices' && (
        <div className="tw-grid tw-gap-6 lg:tw-grid-cols-2">
          <Card title="Device Types">
            {devices.devices.length === 0 ? (
              <EmptyState icon="fa-display" title="No device data yet" />
            ) : (
              <ReactApexChart
                type="pie" height={300}
                options={{ labels: devices.devices.map((d) => d.device_type || 'Unknown'), colors: ['#f26a1b', '#22c55e', '#4b5568', '#f59e0b', '#4cc3f5'], legend: { position: 'bottom' }, stroke: { width: 0 }, dataLabels: { enabled: true } }}
                series={devices.devices.map((d) => d.sessions || 0)}
              />
            )}
          </Card>
          <Card title="Browsers">
            {devices.browsers.length === 0 ? (
              <EmptyState icon="fa-window-maximize" title="No browser data yet" />
            ) : (
              <ReactApexChart
                type="bar" height={300}
                options={{ ...baseChart, plotOptions: { bar: { borderRadius: 6, columnWidth: '45%' } }, xaxis: { categories: devices.browsers.map((b) => b.browser || 'Unknown') } }}
                series={[{ name: 'Sessions', data: devices.browsers.map((b) => b.sessions || 0) }]}
              />
            )}
          </Card>
        </div>
      )}

      {/* BANDWIDTH */}
      {tab === 'bandwidth' && (
        <Card title="Bandwidth Usage">
          <div className="tw-mb-5 tw-grid tw-grid-cols-2 tw-gap-4 lg:tw-grid-cols-4">
            <StatTile icon="fa-arrow-down" tone="good" value={fmtBytes(bandwidth?.total_bandwidth_in || 0)} label="Total In" />
            <StatTile icon="fa-arrow-up" tone="info" value={fmtBytes(bandwidth?.total_bandwidth_out || 0)} label="Total Out" />
            <StatTile icon="fa-database" tone="warn" value={fmtBytes(bandwidth?.total_transferred || 0)} label="Transferred" />
            <StatTile icon="fa-users" tone="brand" value={bandwidth?.peak_viewers || 0} label="Peak Viewers" />
          </div>
          {bwSeries.data.length === 0 ? (
            <EmptyState icon="fa-wifi" title="No bandwidth history yet" hint="Snapshots are recorded every 5 minutes while streams are live." />
          ) : (
            <ReactApexChart
              type="area" height={300}
              options={{ ...areaChart, xaxis: { categories: bwSeries.categories }, yaxis: { labels: { formatter: (v) => `${v} GB` } } }}
              series={[{ name: 'Transferred (GB)', data: bwSeries.data }]}
            />
          )}
        </Card>
      )}

      {/* REVENUE */}
      {tab === 'revenue' && isAdmin && (
        <Card title="Revenue Analytics">
          <div className="tw-mb-5 tw-grid tw-grid-cols-2 tw-gap-4 lg:tw-grid-cols-4">
            <StatTile icon="fa-sack-dollar" tone="good" value={money(revenue?.summary?.successful_revenue)} label="Revenue (paid)" />
            <StatTile icon="fa-receipt" tone="brand" value={revenue?.summary?.total_transactions || 0} label="Transactions" />
            <StatTile icon="fa-circle-xmark" tone="live" value={money(revenue?.summary?.failed_revenue)} label="Failed" />
            <StatTile icon="fa-calculator" tone="warn" value={money(revenue?.summary?.avg_transaction_value)} label="Avg Transaction" />
          </div>
          {revSeries.data.length === 0 ? (
            <EmptyState icon="fa-dollar-sign" title="No revenue in this period" />
          ) : (
            <ReactApexChart
              type="area" height={300}
              options={{ ...areaChart, xaxis: { categories: revSeries.categories }, yaxis: { labels: { formatter: (v) => `$${v}` } } }}
              series={[{ name: 'Revenue ($)', data: revSeries.data }]}
            />
          )}
          {revenue?.by_plan?.length > 0 && (
            <div className="tw-mt-6">
              <Table head={['Plan', 'Revenue', 'Payments']}>
                {revenue.by_plan.map((p, i) => (
                  <Tr key={i}>
                    <Td className="tw-font-semibold tw-text-heading">{p.plan_name}</Td>
                    <Td>{money(p.revenue)}</Td>
                    <Td>{p.subscriptions}</Td>
                  </Tr>
                ))}
              </Table>
            </div>
          )}
        </Card>
      )}
    </>
  );
};

export default connect((state) => ({ isAdmin: selectIsAdmin(state) }))(Analytics);
